<script lang="ts">
  export default {
    name: 'FontSelector',
  };
</script>

<script setup lang="ts">
  import BaseText from '@ui/atoms/baseText/BaseText.vue';
  import { OnClickOutside } from '@vueuse/components';
  import BaseDivider from '@ui/atoms/baseDivider/BaseDivider.vue';
  import { ref, computed, watch, onUnmounted } from 'vue';
  import { RICH_TEXT } from '@/constant/richText';
  import { Font } from '@/components/common/fontSelector/types';
  import { useStore } from '@/store';

  const { value, disabled, fonts, noAutoScroll, noBrandFonts, maxHeight, width } = defineProps({
    value: { type: String, required: true, default: RICH_TEXT.DefaultFontFamily },
    disabled: { type: Boolean, default: false },
    relative: { type: Boolean, default: true },
    fonts: { type: Array, default: () => [] },
    noAutoScroll: { type: Boolean, default: false },
    noBrandFonts: { type: Boolean, default: false },
    maxHeight: { type: Number, default: 500 },
    width: { type: Number, default: 323 },
  });

  const store = useStore();

  const emit = defineEmits<{
    (event: 'update:value', value: string): void;
  }>();

  const userFonts = computed(() => {
    return fonts && fonts.length > 0 ? fonts : store.getters.userFonts;
  });

  const brandFonts = ref(store.getters.getBrandFonts);

  const defaultFonts = ref([RICH_TEXT.DefaultFontFamily]);
  const selectedFont = ref(value);
  const isActive = ref(false);
  const searchQuery = ref('');

  const fontFamilyButton = ref(null);
  const fontsModal = ref(null);
  const searchInput = ref(null);

  const filterAndSortFonts = (fontArray: Font[] = []) => {
    let fonts = [...fontArray];

    if (searchQuery.value) {
      fonts = fonts.filter((font) => {
        const query = searchQuery.value.toLowerCase();
        return (
          font.name.toLowerCase().includes(query) || font.fontName?.toLowerCase().includes(query)
        );
      });
    }

    return fonts.sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredBrandFonts = computed(() => filterAndSortFonts(brandFonts.value));
  const filteredSystemFonts = computed(() =>
    filterAndSortFonts(userFonts.value.filter((font: Font) => font.is_predefined)),
  );
  const filteredImportedFonts = computed(() =>
    filterAndSortFonts(userFonts.value.filter((font: Font) => !font.is_predefined)),
  );

  function showFontFamily(active: boolean) {
    isActive.value = active;

    if (active) {
      searchQuery.value = '';

      // Add auto-scroll functionality when dropdown is opened
      setTimeout(() => {
        const fontsModal = document.querySelector('.fonts-modal');
        if (fontsModal) {
          if (!noAutoScroll) {
            fontsModal.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }

          // Wait for scroll to complete before focusing
          setTimeout(() => {
            if (searchInput.value) {
              (searchInput.value as HTMLInputElement).focus();
            }
          }, 500);
        }
      }, 0);
    }
  }

  function setFontFamily(value: string) {
    showFontFamily(false);
    selectedFont.value = value;
    emit('update:value', value);
  }

  function importFonts() {
    //
  }

  const clickOutsideOptions = ref({
    ignore: [fontFamilyButton.value],
  });

  // Adding event listener for ESC key to close the font selector
  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isActive.value) {
      showFontFamily(false);
      event.stopPropagation();
    }
  }

  watch(isActive, (newValue) => {
    if (newValue) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }
  });

  onUnmounted(() => {
    if (isActive.value) {
      document.removeEventListener('keydown', handleKeyDown);
    }
  });
</script>

<template>
  <div class="select-container">
    <button
      class="select-button"
      ref="fontFamilyButton"
      @click="showFontFamily(!isActive)"
      :disabled="disabled"
    >
      <BaseText :style="{ fontFamily: value }">{{ value }}</BaseText>
      <i class="material-icons">keyboard_arrow_down</i>
    </button>
    <transition name="fade-top">
      <OnClickOutside
        v-if="isActive"
        class="fonts-modal scrollbar"
        :style="{ width: `${width}px`, maxHeight: `${maxHeight}px` }"
        :options="clickOutsideOptions"
        @trigger="setFontFamily(value)"
        ref="fontsModal"
        v-on:keydown.esc="showFontFamily(false)"
      >
        <div class="search-container">
          <i class="material-icons search-icon">search</i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search"
            class="search-input"
            ref="searchInput"
          />
        </div>

        <div class="fonts-content">
          <!-- Brand Fonts -->
          <div v-if="!noBrandFonts" class="fontfamily-section">
            <div class="section-title">Brand Fonts</div>
            <div
              class="font-item"
              :title="font.name"
              v-for="font in filteredBrandFonts"
              :key="font.id"
              @click="setFontFamily(font.name)"
            >
              <div class="font-preview">T</div>
              <BaseText :style="{ fontFamily: font.name }">{{ font.fontName }}</BaseText>
            </div>

            <div v-if="filteredBrandFonts.length === 0" class="no-fonts-message">No Fonts</div>
          </div>

          <div class="fontfamily-section">
            <div class="section-header">
              <div class="section-title">Imported</div>
            </div>

            <div v-if="filteredImportedFonts.length === 0" class="no-fonts-message">No Fonts</div>

            <template v-else>
              <div
                class="font-item"
                v-for="font in filteredImportedFonts"
                :key="font.id"
                @click="setFontFamily(font.name)"
              >
                <div class="font-preview">T</div>
                <BaseText :style="{ fontFamily: font.name }">{{ font.name }}</BaseText>
              </div>
            </template>
          </div>

          <!-- System Fonts -->
          <div class="fontfamily-section">
            <div class="section-title">System Fonts</div>

            <div v-if="filteredSystemFonts.length === 0" class="no-fonts-message">No Fonts</div>

            <template v-else>
              <div
                class="font-item"
                v-for="font in filteredSystemFonts"
                :key="font.id"
                @click="setFontFamily(font.name)"
              >
                <div class="font-preview">T</div>
                <BaseText :style="{ fontFamily: font.name }">{{ font.name }}</BaseText>
              </div>
            </template>
          </div>
        </div>
      </OnClickOutside>
    </transition>
  </div>
</template>

<style scoped lang="scss">
  .select-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    margin: 0 -2px 0 2px;
    padding: 4px 8px;
    box-shadow: 0 0 0 1px $borderGrey;
    border-radius: 8px;
    border: 2px solid transparent;
    background-color: white;
    transition: all 0.2s ease-in-out;

    > :first-child {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      float: left;
      max-width: 85%;
    }

    > :last-child {
      float: right;
      max-width: 15%;
    }
  }

  .select-container {
    width: 100%;
    height: 38px;
    position: relative;

    .fade-top-enter-active,
    .fade-top-leave-active {
      transition: all 0.3s ease;
    }

    .fade-top-enter {
      opacity: 0;
      transform: translateY(-20px);
    }

    .fade-top-leave-to {
      opacity: 0;
      transform: translateY(20px);
    }
  }

  .fonts-modal {
    z-index: 1000;
    position: absolute;
    top: 48px;
    left: 1px;
    overflow-y: auto;
    background-color: white;
    border-radius: 8px;
    border: 1px solid $borderGrey;
    display: flex;
    flex-direction: column;
  }

  .fonts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid $borderGrey;

    h2 {
      font-size: 24px;
      font-weight: 500;
      margin: 0;
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: $lineGrey2;
      }

      i {
        font-size: 24px;
      }
    }
  }

  .search-container {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
    padding: 12px 16px;

    .search-icon {
      position: absolute;
      left: 30px;
      top: 50%;
      transform: translateY(-50%);
      color: $secondaryText;
    }

    .search-input {
      width: 100%;
      height: 40px;
      border-radius: 24px;
      border: 1px solid $borderGrey;
      padding: 0 16px 0 40px;
      font-size: 16px;
      outline: none;

      &:focus {
        border-color: $borderGrey2;
      }
    }
  }

  .fonts-content {
    padding: 0 0 20px;
  }

  .fontfamily-section {
    margin-bottom: 24px;
  }

  .section-title {
    padding: 4px 20px;
    margin-bottom: 8px;
    width: 100%;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    color: $tableText;
    background-color: $fillGrey2;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .import-button {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: $primaryText;
    cursor: pointer;
    font-size: 14px;

    i {
      font-size: 18px;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  .font-item {
    display: flex;
    align-items: center;
    padding: 8px 20px;
    cursor: pointer;
    gap: 12px;

    &:hover {
      color: $primaryRed;
    }
  }

  .font-preview {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: $lineGrey;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 4px;
  }

  .no-fonts-message {
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $secondaryText;
    font-style: italic;
  }
</style>
